import { useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { ITERABLE_WEB_API_KEY } from '@m/config/env';

import IterableService from './IterableService';
import {
  fetchIterableJwtCredentials,
  iterableJwtQueryKeys,
  STALE_TIME_MS,
  useIterableJwtQuery,
} from './useIterableJwtQuery';

import type { GenerateJWTPayload } from '@iterable/web-sdk';
import type { User } from '@m/types/api';

/**
 * Initializes Iterable Web SDK after the signed-in user JWT is loaded from the site API.
 *
 * Backend: GET /api/iterable/jwt (see SITE_API_BASE_URL + /iterable/jwt) returns the user JWT only.
 * The SDK’s first argument is the Iterable Web API key from `REACT_APP_ITERABLE_WEB_API_KEY`.
 *
 * Identifies with `setUserID` (numeric id) so it matches JWT `userId` claims.
 * When the app has an email, calls `updateUser` with `dataFields.email` so Iterable stores it on that profile.
 */
export function useIterable({
  authenticated,
  /** When false, skips JWT fetch and Iterable SDK init (e.g. feature flag off). */
  enabled = true,
  user,
}: {
  authenticated: boolean | null;
  enabled?: boolean;
  user: User | null;
}) {
  const [initializedForUserId, setInitializedForUserId] = useState<number | null>(null);
  const blockedForUserIdRef = useRef<number | null>(null);
  const initializingForUserIdRef = useRef<number | null>(null);
  const queryClient = useQueryClient();
  const userId = user?.id;

  const jwtQuery = useIterableJwtQuery({
    userId,
    enabled: enabled && authenticated === true && !!user,
  });

  useEffect(() => {
    if (authenticated !== true) {
      queueMicrotask(() => {
        setInitializedForUserId(null);
        blockedForUserIdRef.current = null;
        initializingForUserIdRef.current = null;
      });
    }
  }, [authenticated]);

  useEffect(() => {
    if (!enabled) {
      queueMicrotask(() => {
        setInitializedForUserId(null);
        blockedForUserIdRef.current = null;
        initializingForUserIdRef.current = null;
      });
    }
  }, [enabled]);

  const isInitialized =
    enabled && authenticated === true && !!user && initializedForUserId === user.id;

  useEffect(() => {
    if (!enabled || !authenticated || !user || !jwtQuery.isSuccess || !jwtQuery.data) {
      return;
    }
    if (initializedForUserId === user.id) {
      return;
    }
    if (blockedForUserIdRef.current === user.id) {
      return;
    }
    if (initializingForUserIdRef.current === user.id) {
      return;
    }

    const apiKey = ITERABLE_WEB_API_KEY;
    if (!apiKey) {
      return;
    }
    initializingForUserIdRef.current = user.id;

    const initialJwt = jwtQuery.data.jwt;
    let hasReturnedInitialJwt = false;

    const generateJWT = async (_payload: GenerateJWTPayload): Promise<string> => {
      if (!hasReturnedInitialJwt) {
        hasReturnedInitialJwt = true;
        return initialJwt;
      }

      const fresh = await queryClient.fetchQuery({
        queryKey: iterableJwtQueryKeys.detail(user.id),
        queryFn: fetchIterableJwtCredentials,
        staleTime: STALE_TIME_MS,
      });
      return fresh.jwt;
    };

    try {
      const instance = IterableService.initializeWithJWT(apiKey, generateJWT);

      if (!instance) {
        blockedForUserIdRef.current = user.id;
        initializingForUserIdRef.current = null;
        // eslint-disable-next-line no-console -- Iterable init failure is useful in dev
        console.warn('Iterable SDK initialize() returned null.');
        return;
      }

      const email = user.email?.trim();

      instance
        .setUserID(String(user.id))
        .then(() => {
          if (!email) {
            return Promise.resolve();
          }
          return IterableService.updateUserDataFields({ email }).catch((err: unknown) => {
            const msg = err instanceof Error ? err.message : String(err);
            // eslint-disable-next-line no-console -- Iterable updateUser failures are non-fatal
            console.warn('Iterable updateUser (email) failed:', msg);
          });
        })
        .then(() => {
          setInitializedForUserId(user.id);
          initializingForUserIdRef.current = null;
        })
        .catch((err) => {
          // eslint-disable-next-line no-console -- identification errors are non-fatal
          console.warn('Iterable identification failed:', err?.message ?? err);
          instance.clearRefresh();
          blockedForUserIdRef.current = user.id;
          initializingForUserIdRef.current = null;
        });
    } catch (error) {
      blockedForUserIdRef.current = user.id;
      initializingForUserIdRef.current = null;
      // eslint-disable-next-line no-console -- init errors should surface in console
      console.error('Failed to initialize Iterable SDK:', error);
    }
  }, [
    authenticated,
    enabled,
    user,
    jwtQuery.isSuccess,
    jwtQuery.data,
    initializedForUserId,
    queryClient,
  ]);

  return {
    isInitialized,
    trackEvent: IterableService.track,
  };
}
